import { z } from 'zod';
import { calendarDateSchema } from '@/features/listing-filters/calendarDate';

export const REPORTING_GRANULARITIES = ['hour', 'day', 'week'] as const;

/**
 * Every value is optional, and an absent one means "use the default" rather
 * than "empty". That matters here because this page opens on the last 7 days:
 * if the defaults were written into the URL, no URL would ever look bare and
 * saved filters would never be restored.
 */
export const reportingFiltersSearchSchema = z.object({
  startDate: calendarDateSchema.optional().catch(undefined),
  endDate: calendarDateSchema.optional().catch(undefined),
  templateId: z.string().min(1).optional().catch(undefined),
  status: z
    .enum(['Pending', 'Running', 'Completed', 'Failed', 'Cancelled'])
    .optional()
    .catch(undefined),
  executionMode: z.enum(['Full', 'PersistOnly', 'DryRun']).optional().catch(undefined),
  granularity: z.enum(REPORTING_GRANULARITIES).optional().catch(undefined),
});

export type ReportingFilterValues = z.infer<typeof reportingFiltersSearchSchema>;

export const REPORTING_DEFAULT_DAYS = 7;
export const REPORTING_DEFAULT_GRANULARITY = 'day' as const;
export const REPORTING_DATE_PRESETS = [7, 30, 90] as const;

const DAY_MS = 24 * 60 * 60 * 1000;

export function formatDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

export function daysBefore(now: Date, days: number): string {
  return formatDate(new Date(now.getTime() - days * DAY_MS));
}

export function activeDatePreset(
  startDate: string,
  endDate: string,
  now: Date
): (typeof REPORTING_DATE_PRESETS)[number] | null {
  if (endDate !== formatDate(now)) return null;
  return REPORTING_DATE_PRESETS.find((days) => startDate === daysBefore(now, days)) ?? null;
}
